import { z } from 'zod'

import { isCalendarDate } from '../utils/calendar-date'

const calendarDay = z.string().refine(isCalendarDate, 'Expected YYYY-MM-DD')
const maximumWorkItems = 10_000
const workIds = z.array(z.string().uuid()).max(maximumWorkItems)

export const BillingRunPeriodSchema = z
  .object({
    periodStart: calendarDay.optional(),
    periodEnd: calendarDay.optional(),
  })
  .refine(
    value =>
      !value.periodStart ||
      !value.periodEnd ||
      value.periodStart <= value.periodEnd,
    { message: 'Billing period ends before it starts', path: ['periodEnd'] },
  )

/** Bounded immutable intent; the server still resolves current eligible work. */
export const CreateBillingRunSchema = z
  .object({
    entryIds: workIds,
    expenseIds: workIds.optional(),
    invoiceMode: z.enum(['client', 'project']).optional(),
    grouping: z.enum(['memberRole', 'member', 'role', 'project']).optional(),
    periodStart: calendarDay.optional(),
    periodEnd: calendarDay.optional(),
    notes: z.string().max(10_000).optional(),
    issueDate: calendarDay.optional(),
    dueDate: calendarDay.optional(),
  })
  .strict()
  .superRefine((value, context) => {
    const count = value.entryIds.length + (value.expenseIds?.length ?? 0)
    if (count === 0 || count > maximumWorkItems)
      context.addIssue({
        code: 'custom',
        message: `Select between 1 and ${maximumWorkItems} work items`,
        path: ['entryIds'],
      })
    if (
      value.periodStart &&
      value.periodEnd &&
      value.periodStart > value.periodEnd
    )
      context.addIssue({
        code: 'custom',
        message: 'Billing period ends before it starts',
        path: ['periodEnd'],
      })
    if (value.issueDate && value.dueDate && value.dueDate < value.issueDate)
      context.addIssue({
        code: 'custom',
        message: 'Invoice due date precedes its issue date',
        path: ['dueDate'],
      })
  })

export type CreateBillingRunPayload = z.infer<typeof CreateBillingRunSchema>

/** Recovery routes never accept an anonymous operation or adopt another account. */
export const BillingRunRequestSchema = z
  .object({
    operationId: z.string().uuid(),
    expectedUserId: z.string().uuid(),
    input: CreateBillingRunSchema,
  })
  .strict()
export type BillingRunRequest = z.infer<typeof BillingRunRequestSchema>

/** Only immutable links and ownership cross the writer boundary. */
export const BillingRunOutcomeSchema = z
  .object({
    version: z.literal(1),
    organizationId: z.string().uuid(),
    userId: z.string().uuid(),
    entityId: z.string().uuid(),
    operationId: z.string().uuid(),
    outcome: z.enum(['APPLIED', 'CANCELLED']),
    invoiceIds: z.array(z.string().uuid()).max(maximumWorkItems),
  })
  .strict()
  .superRefine((value, context) => {
    if (
      new Set(value.invoiceIds).size !== value.invoiceIds.length ||
      (value.outcome === 'APPLIED'
        ? value.invoiceIds.length === 0
        : value.invoiceIds.length !== 0)
    )
      context.addIssue({
        code: 'custom',
        path: ['invoiceIds'],
        message: 'Invalid billing request outcome',
      })
  })
export type BillingRunOutcome = z.infer<typeof BillingRunOutcomeSchema>
